/**
 * Bengaluru verdict seed data.
 * Hand-seeded 20 starter neighborhoods — minimal viable scaffolding so
 * the city renders end-to-end. Verdict copy is deliberately conservative
 * ("amber" by default, observable facts only) until per-suburb research
 * lands. Replace with researched signals once verdict pipeline ships.
 *
 * Schema is shared with verdicts.ts (HYD) — same `Verdict` interface.
 *
 * What this file IS:
 *   - 20 well-known BLR suburbs with coords + a 3-line conservative verdict
 *   - Tags for filter/sort surfaces
 *   - Empty sources[] (placeholder — fill as research lands)
 *
 * What this file IS NOT (yet):
 *   - Researched price medians (priceData.* — will fall through to band logic)
 *   - Investor evidence tiers (investorEvidence — undefined → generic scoring)
 *   - Any "best of" claim — this is a starter set, not a curated picks list
 */

import type { Verdict } from "./verdicts";
import { BLR_SUBURB_PRICING, getPricingFor } from "./suburb-pricing";

export const BLR_CENTER: [number, number] = [77.5946, 12.9716];

const _BLR_RAW: Verdict[] = [
  {
    id: "blr-indiranagar",
    name: "Indiranagar",
    coord: [77.6390, 12.9719],
    verdict: "green",
    reasons: [
      "Walkable high street: cafés, breweries, retail along 100ft Road",
      "100ft Road metro (Indiranagar) on Purple Line — direct CBD access",
      "Weekend traffic + parking pressure can be heavy",
    ],
    priceRange: "₹12k–22k / sqft",
    confidenceScore: 60,
    tags: ["central", "walkable", "premium", "young-professionals"],
    sources: [],
  },
  {
    id: "blr-koramangala",
    name: "Koramangala",
    coord: [77.6101, 12.9352],
    verdict: "green",
    reasons: [
      "Startup hub: Forum Mall, restaurants, workshare across blocks",
      "10–20 min to most south/east IT campuses",
      "Inner-ring traffic snarls in evening rush",
    ],
    priceRange: "₹13k–22k / sqft",
    confidenceScore: 60,
    tags: ["central", "premium", "young-professionals", "tech-hub"],
    sources: [],
  },
  {
    id: "blr-whitefield",
    name: "Whitefield",
    coord: [77.7499, 12.9698],
    verdict: "amber",
    reasons: [
      "Major IT campus cluster: ITPL, EPIP, Brookfields nearby",
      "Whitefield metro (Purple Line east end) operational since 2023",
      "Long commute to central Bengaluru in evening peak",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 60,
    tags: ["it-corridor", "family-friendly", "tech-hub"],
    sources: [],
  },
  {
    id: "blr-electronic-city",
    name: "Electronic City",
    coord: [77.6601, 12.8458],
    verdict: "amber",
    reasons: [
      "Anchored by Infosys, Wipro, Biocon, HCL campuses",
      "Hosur Road elevated expressway (NICE) cuts central commute",
      "Limited walkable retail/social density vs central pockets",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 60,
    tags: ["it-corridor", "outskirts", "family-friendly"],
    sources: [],
  },
  {
    id: "blr-marathahalli",
    name: "Marathahalli",
    coord: [77.6968, 12.9591],
    verdict: "amber",
    reasons: [
      "Mid-corridor between Sarjapur, Whitefield, ORR tech campuses",
      "ORR signal (Marathahalli junction) is a known evening bottleneck",
      "Wide choice of 1–3BHK rentals across price bands",
    ],
    priceRange: "₹6k–9k / sqft",
    confidenceScore: 55,
    tags: ["it-corridor", "mid-market"],
    sources: [],
  },
  {
    id: "blr-hsr-layout",
    name: "HSR Layout",
    coord: [77.6411, 12.9081],
    verdict: "green",
    reasons: [
      "Planned grid of sectors — wide tree-lined streets, dense F&B",
      "Equidistant to Koramangala, Sarjapur, Bommanahalli tech zones",
      "Apartment supply tight; limited new-build inventory",
    ],
    priceRange: "₹9k–15k / sqft",
    confidenceScore: 60,
    tags: ["family-friendly", "premium", "planned-grid"],
    sources: [],
  },
  {
    id: "blr-btm-layout",
    name: "BTM Layout",
    coord: [77.6101, 12.9166],
    verdict: "amber",
    reasons: [
      "Affordable mid-market between Koramangala and Jayanagar",
      "Good metro access (Silk Board interchange nearby)",
      "Internal lane density limits 4-wheeler parking",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 55,
    tags: ["mid-market", "central"],
    sources: [],
  },
  {
    id: "blr-jayanagar",
    name: "Jayanagar",
    coord: [77.5938, 12.9250],
    verdict: "green",
    reasons: [
      "Established residential layout — wide roads, parks, schools",
      "Jayanagar metro (Yellow Line) opened to silk-board interchange",
      "South-Bengaluru anchor: less IT-corridor, more family",
    ],
    priceRange: "₹10k–16k / sqft",
    confidenceScore: 60,
    tags: ["family-friendly", "established", "south-blr"],
    sources: [],
  },
  {
    id: "blr-jp-nagar",
    name: "JP Nagar",
    coord: [77.5800, 12.9081],
    verdict: "green",
    reasons: [
      "Adjacent to Jayanagar with newer apartment density",
      "Phase-by-phase planning gives different price bands within suburb",
      "Outer Ring Road access — south-east tech corridor reachable",
    ],
    priceRange: "₹8k–13k / sqft",
    confidenceScore: 55,
    tags: ["family-friendly", "south-blr", "mid-market"],
    sources: [],
  },
  {
    id: "blr-mg-road",
    name: "MG Road",
    coord: [77.6088, 12.9759],
    verdict: "amber",
    reasons: [
      "Central business district — restaurants, retail, hotels",
      "MG Road metro (Purple Line) — single-seat commute to Whitefield",
      "Limited residential stock; mostly commercial + serviced apartments",
    ],
    priceRange: "₹15k–25k / sqft",
    confidenceScore: 55,
    tags: ["cbd", "premium", "commercial-heavy"],
    sources: [],
  },
  {
    id: "blr-hebbal",
    name: "Hebbal",
    coord: [77.5915, 13.0358],
    verdict: "amber",
    reasons: [
      "Direct flyover access to Kempegowda International Airport",
      "Manyata Tech Park anchor — north-Bengaluru jobs hub",
      "Outer Ring Road (Hebbal flyover) jams in peak hours",
    ],
    priceRange: "₹8k–14k / sqft",
    confidenceScore: 55,
    tags: ["it-corridor", "north-blr", "family-friendly"],
    sources: [],
  },
  {
    id: "blr-yelahanka",
    name: "Yelahanka",
    coord: [77.5946, 13.1007],
    verdict: "amber",
    reasons: [
      "North-Bengaluru — closest urban node to airport",
      "Lower density, more standalone homes / villas",
      "Distance from south/east IT corridors is real for daily commute",
    ],
    priceRange: "₹5k–9k / sqft",
    confidenceScore: 55,
    tags: ["outskirts", "family-friendly", "north-blr"],
    sources: [],
  },
  {
    id: "blr-sarjapur",
    name: "Sarjapur",
    coord: [77.7872, 12.8576],
    verdict: "amber",
    reasons: [
      "Sarjapur Road corridor: IT campuses + apartment new-build cluster",
      "Wipro, Accenture, Cisco campuses within 20 min off-peak",
      "Last-mile from main road into projects can be unpaved",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 55,
    tags: ["it-corridor", "south-east", "new-build"],
    sources: [],
  },
  {
    id: "blr-bellandur",
    name: "Bellandur",
    coord: [77.6741, 12.9258],
    verdict: "amber",
    reasons: [
      "Outer Ring Road IT belt — Bagmane Tech Park, Ecospace cluster",
      "Bellandur lake foam events have been historically problematic",
      "ORR jams in both directions in peak hours",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 55,
    tags: ["it-corridor", "lake-adjacent", "tech-hub"],
    sources: [],
  },
  {
    id: "blr-malleshwaram",
    name: "Malleshwaram",
    coord: [77.5713, 13.0034],
    verdict: "green",
    reasons: [
      "Heritage layout — temples, markets, tree-lined cross streets",
      "Strong school cluster (Vidya Mandir, MES) and walkable amenities",
      "Limited new apartment stock; mostly mid-rise + standalone homes",
    ],
    priceRange: "₹11k–17k / sqft",
    confidenceScore: 55,
    tags: ["heritage", "family-friendly", "north-blr", "established"],
    sources: [],
  },
  {
    id: "blr-banashankari",
    name: "Banashankari",
    coord: [77.5566, 12.9249],
    verdict: "green",
    reasons: [
      "Established south-west residential pocket; phase 1–6 spread",
      "Good schools and metro Yellow-Line access (Banashankari station)",
      "Distance from east-Bengaluru IT corridors is real",
    ],
    priceRange: "₹7k–12k / sqft",
    confidenceScore: 55,
    tags: ["family-friendly", "south-blr", "established"],
    sources: [],
  },
  {
    id: "blr-basavanagudi",
    name: "Basavanagudi",
    coord: [77.5717, 12.9420],
    verdict: "amber",
    reasons: [
      "Old Bengaluru character — Bull Temple, Gandhi Bazaar markets",
      "Narrow streets and low parking density",
      "Strong family-stock; rental supply tight",
    ],
    priceRange: "₹10k–15k / sqft",
    confidenceScore: 50,
    tags: ["heritage", "central", "family-friendly"],
    sources: [],
  },
  {
    id: "blr-ulsoor",
    name: "Ulsoor",
    coord: [77.6196, 12.9784],
    verdict: "amber",
    reasons: [
      "Adjacent to MG Road — very central, lake + parade ground",
      "Mixed building stock: heritage + mid-rise apartments",
      "Limited modern amenity density vs Indiranagar/Koramangala",
    ],
    priceRange: "₹10k–18k / sqft",
    confidenceScore: 50,
    tags: ["central", "lake-adjacent", "heritage"],
    sources: [],
  },
  {
    id: "blr-kengeri",
    name: "Kengeri",
    coord: [77.4839, 12.9085],
    verdict: "amber",
    reasons: [
      "South-west Bengaluru on the Mysore Road / Highway",
      "Affordable apartment + plot inventory; lower price band",
      "Long commute to east/south-east IT corridors",
    ],
    priceRange: "₹4k–7k / sqft",
    confidenceScore: 45,
    tags: ["outskirts", "affordable", "south-west"],
    sources: [],
  },
  {
    id: "blr-rajajinagar",
    name: "Rajajinagar",
    coord: [77.5530, 12.9971],
    verdict: "green",
    reasons: [
      "Planned western layout — Orion Mall, Iskcon temple landmarks",
      "Rajajinagar metro (Green Line / Purple Line interchange nearby)",
      "Mature schools and family infrastructure",
    ],
    priceRange: "₹9k–14k / sqft",
    confidenceScore: 55,
    tags: ["family-friendly", "west-blr", "established"],
    sources: [],
  },

  // ---- Phase-2 expansion (30 more well-known neighbourhoods) -------
  // Hand-seeded so the BLR map has comparable density to HYD/MEL on
  // launch. Same conservative-verdict convention as Phase-1: green only
  // where the suburb is clearly established with diversified amenity;
  // amber for everything that is single-corridor / commute-dependent /
  // edge-of-city. Coords from OpenStreetMap area centroids.
  {
    id: "blr-bommanahalli",
    name: "Bommanahalli",
    coord: [77.6195, 12.9080],
    verdict: "amber",
    reasons: [
      "Hosur Road junction — gateway between Koramangala and Electronic City",
      "Apartment supply across price bands; metro Yellow Line along corridor",
      "Through-traffic load on inner Hosur Road is constant, peak or off",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 50,
    tags: ["it-corridor", "south-east", "mid-market"],
    sources: [],
  },
  {
    id: "blr-frazer-town",
    name: "Frazer Town",
    coord: [77.6131, 12.9988],
    verdict: "green",
    reasons: [
      "Cantonment-era layout — wide tree-lined streets, churches, cafés",
      "Walkable to MG Road / Cantonment station for east-west commute",
      "Heritage stock — limited new-build apartment inventory",
    ],
    priceRange: "₹13k–20k / sqft",
    confidenceScore: 55,
    tags: ["heritage", "central", "walkable", "premium"],
    sources: [],
  },
  {
    id: "blr-richmond-town",
    name: "Richmond Town",
    coord: [77.6000, 12.9590],
    verdict: "green",
    reasons: [
      "Old CBD residential — embassies, schools, walkable to MG Road",
      "Richmond Circle metro on Pink Line gives single-seat north-south",
      "Property mostly heritage / standalone — premium plot prices",
    ],
    priceRange: "₹16k–25k / sqft",
    confidenceScore: 55,
    tags: ["central", "premium", "heritage", "walkable"],
    sources: [],
  },
  {
    id: "blr-sadashivanagar",
    name: "Sadashivanagar",
    coord: [77.5810, 13.0080],
    verdict: "green",
    reasons: [
      "Premium north-west plot layout — politicians, industrialists",
      "Adjacent to Sankey Tank / Palace Grounds green belt",
      "Almost entirely standalone homes — apartment supply minimal",
    ],
    priceRange: "₹20k–35k / sqft",
    confidenceScore: 55,
    tags: ["premium", "north-blr", "low-density", "established"],
    sources: [],
  },
  {
    id: "blr-vasanth-nagar",
    name: "Vasanth Nagar",
    coord: [77.5921, 12.9907],
    verdict: "green",
    reasons: [
      "Central north — heritage bungalows, hotels, embassies",
      "Walkable to Cubbon Park / Vidhana Soudha government core",
      "Limited rental stock; mostly serviced apartments",
    ],
    priceRange: "₹15k–24k / sqft",
    confidenceScore: 50,
    tags: ["central", "premium", "heritage"],
    sources: [],
  },
  {
    id: "blr-brigade-road",
    name: "Brigade Road",
    coord: [77.6076, 12.9716],
    verdict: "amber",
    reasons: [
      "Retail + nightlife spine of central Bengaluru",
      "MG Road metro one block away; pedestrian zone weekends",
      "Largely commercial — residential stock thin and noisy",
    ],
    priceRange: "₹16k–26k / sqft",
    confidenceScore: 45,
    tags: ["cbd", "commercial-heavy", "central"],
    sources: [],
  },
  {
    id: "blr-domlur",
    name: "Domlur",
    coord: [77.6385, 12.9610],
    verdict: "green",
    reasons: [
      "Inner-east — between Indiranagar, Koramangala, ORR tech belt",
      "Embassy stretch + Diamond District commercial parks",
      "Limited new-build supply; demand keeps prices firm",
    ],
    priceRange: "₹12k–18k / sqft",
    confidenceScore: 55,
    tags: ["central", "it-adjacent", "premium"],
    sources: [],
  },
  {
    id: "blr-hennur",
    name: "Hennur",
    coord: [77.6356, 13.0428],
    verdict: "amber",
    reasons: [
      "North-east emerging corridor — apartment new-build cluster",
      "Hennur Road connects to airport via Bellary / Thanisandra route",
      "Last-mile from main road into projects can be unpaved",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 45,
    tags: ["north-east", "new-build", "emerging"],
    sources: [],
  },
  {
    id: "blr-banaswadi",
    name: "Banaswadi",
    coord: [77.6504, 13.0125],
    verdict: "amber",
    reasons: [
      "Established north-east residential — Outer Ring Road access",
      "Mix of standalone homes, mid-rise apartments, schools",
      "Through-traffic on the ORR can affect inner-lane noise",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 50,
    tags: ["family-friendly", "north-east", "established"],
    sources: [],
  },
  {
    id: "blr-kalyan-nagar",
    name: "Kalyan Nagar",
    coord: [77.6420, 13.0270],
    verdict: "green",
    reasons: [
      "Planned HRBR layout — wide roads, parks, schools",
      "Walkable F&B strip; equidistant to airport + east tech",
      "Apartment supply tight; single-family demand keeps prices firm",
    ],
    priceRange: "₹8k–13k / sqft",
    confidenceScore: 55,
    tags: ["family-friendly", "north-east", "planned-grid"],
    sources: [],
  },
  {
    id: "blr-mahadevapura",
    name: "Mahadevapura",
    coord: [77.6948, 12.9905],
    verdict: "amber",
    reasons: [
      "Heart of east Bengaluru tech corridor — ORR campuses adjacent",
      "Phoenix Marketcity, mall + multiplex anchor amenity stack",
      "ORR jams in both directions during peak hours",
    ],
    priceRange: "₹7k–12k / sqft",
    confidenceScore: 55,
    tags: ["it-corridor", "east-blr", "tech-hub"],
    sources: [],
  },
  {
    id: "blr-thanisandra",
    name: "Thanisandra",
    coord: [77.6244, 13.0584],
    verdict: "amber",
    reasons: [
      "Manyata-adjacent residential cluster — apartment new-builds",
      "ORR + Bellary Road dual access to airport / Manyata Tech Park",
      "Internal feeder roads still being widened in stretches",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 50,
    tags: ["it-adjacent", "north-blr", "new-build"],
    sources: [],
  },
  {
    id: "blr-kr-puram",
    name: "KR Puram",
    coord: [77.6985, 12.9985],
    verdict: "amber",
    reasons: [
      "Eastern gateway to Whitefield — Purple Line metro extension",
      "Bridge bottleneck (KR Puram flyover) is a known peak chokepoint",
      "Affordable apartment + plot inventory across the corridor",
    ],
    priceRange: "₹5k–9k / sqft",
    confidenceScore: 50,
    tags: ["it-corridor", "east-blr", "affordable"],
    sources: [],
  },
  {
    id: "blr-varthur",
    name: "Varthur",
    coord: [77.7407, 12.9420],
    verdict: "amber",
    reasons: [
      "South-east of Whitefield — apartment new-builds along the lake",
      "Sarjapur / Outer Ring Road both reachable for IT commute",
      "Varthur lake foam events have been historically problematic",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 45,
    tags: ["it-adjacent", "lake-adjacent", "new-build"],
    sources: [],
  },
  {
    id: "blr-bommasandra",
    name: "Bommasandra",
    coord: [77.6991, 12.8125],
    verdict: "amber",
    reasons: [
      "South industrial belt — Biocon, Narayana Hrudayalaya, KIADB hub",
      "Yellow Line metro (under construction) will link to RV Road",
      "Industrial zoning means F&B / retail density is thin",
    ],
    priceRange: "₹4k–7k / sqft",
    confidenceScore: 45,
    tags: ["industrial", "south-edge", "affordable"],
    sources: [],
  },
  {
    id: "blr-attibele",
    name: "Attibele",
    coord: [77.7710, 12.7790],
    verdict: "amber",
    reasons: [
      "Karnataka–Tamil Nadu border on Hosur Road",
      "Industrial cluster + emerging villa + plot projects",
      "True outskirts — daily commute to central Bengaluru is real",
    ],
    priceRange: "₹3k–6k / sqft",
    confidenceScore: 40,
    tags: ["outskirts", "industrial", "affordable", "south-edge"],
    sources: [],
  },
  {
    id: "blr-anekal",
    name: "Anekal",
    coord: [77.6967, 12.7090],
    verdict: "amber",
    reasons: [
      "Taluk-level node south of Bengaluru — plot + farmhouse market",
      "Low density, lower prices, longer travel times",
      "Daily commute to north / central Bengaluru is impractical",
    ],
    priceRange: "₹2k–5k / sqft",
    confidenceScore: 35,
    tags: ["outskirts", "low-density", "affordable"],
    sources: [],
  },
  {
    id: "blr-bidadi",
    name: "Bidadi",
    coord: [77.3857, 12.7960],
    verdict: "amber",
    reasons: [
      "South-west industrial node on Mysore Road / NH-275",
      "Toyota Kirloskar + Coca-Cola plants anchor the local economy",
      "Far from city core; suited to plant-employed residents",
    ],
    priceRange: "₹2k–5k / sqft",
    confidenceScore: 35,
    tags: ["outskirts", "industrial", "south-west"],
    sources: [],
  },
  {
    id: "blr-nelamangala",
    name: "Nelamangala",
    coord: [77.3940, 13.0995],
    verdict: "amber",
    reasons: [
      "North-west edge on NH-48 (Tumkur Road)",
      "Logistics + warehousing belt; villa + plot inventory",
      "Far from east / south IT corridors — long commute",
    ],
    priceRange: "₹3k–6k / sqft",
    confidenceScore: 35,
    tags: ["outskirts", "industrial", "north-west"],
    sources: [],
  },
  {
    id: "blr-devanahalli",
    name: "Devanahalli",
    coord: [77.7080, 13.2480],
    verdict: "amber",
    reasons: [
      "Airport adjacency — KIA + aerospace SEZ + business park cluster",
      "Premium villa + plot projects priced for second-home buyers",
      "Daily commute to central Bengaluru is 60–90 min off-peak",
    ],
    priceRange: "₹4k–9k / sqft",
    confidenceScore: 45,
    tags: ["outskirts", "airport-adjacent", "north-blr"],
    sources: [],
  },
  {
    id: "blr-yeshwanthpur",
    name: "Yeshwanthpur",
    coord: [77.5520, 13.0287],
    verdict: "amber",
    reasons: [
      "Major railway junction + APMC market hub",
      "Yeshwanthpur metro (Green Line) for north-south commute",
      "Industrial + commercial mix limits residential quietness",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 50,
    tags: ["transit-hub", "north-west", "mid-market"],
    sources: [],
  },
  {
    id: "blr-peenya",
    name: "Peenya",
    coord: [77.5180, 13.0260],
    verdict: "amber",
    reasons: [
      "One of Asia's largest industrial estates — manufacturing hub",
      "Peenya metro (Green Line) connects to north-south corridor",
      "Industrial zoning: residential pockets sit between factories",
    ],
    priceRange: "₹5k–8k / sqft",
    confidenceScore: 45,
    tags: ["industrial", "north-west", "affordable"],
    sources: [],
  },
  {
    id: "blr-kumaraswamy-layout",
    name: "Kumaraswamy Layout",
    coord: [77.5598, 12.9079],
    verdict: "amber",
    reasons: [
      "Established south-west residential layout — schools + parks",
      "Yelachenahalli metro (Yellow Line) for inner-south commute",
      "Distance from east-Bengaluru IT corridors is real",
    ],
    priceRange: "₹6k–10k / sqft",
    confidenceScore: 50,
    tags: ["family-friendly", "south-blr", "established"],
    sources: [],
  },
  {
    id: "blr-padmanabhanagar",
    name: "Padmanabhanagar",
    coord: [77.5557, 12.9165],
    verdict: "green",
    reasons: [
      "South-west residential stretch — wide roads, schools, temples",
      "Equidistant to Banashankari, Jayanagar, Kanakapura Road",
      "Apartment supply limited; mostly standalone + duplex stock",
    ],
    priceRange: "₹7k–12k / sqft",
    confidenceScore: 50,
    tags: ["family-friendly", "south-blr", "established"],
    sources: [],
  },
  {
    id: "blr-rt-nagar",
    name: "RT Nagar",
    coord: [77.5972, 13.0210],
    verdict: "amber",
    reasons: [
      "North-central established layout — markets, schools, Sultan Palya",
      "Bellary Road + ORR access; airport corridor",
      "Internal lane density limits 4-wheeler parking",
    ],
    priceRange: "₹7k–11k / sqft",
    confidenceScore: 50,
    tags: ["family-friendly", "north-central", "established"],
    sources: [],
  },
  {
    id: "blr-cv-raman-nagar",
    name: "CV Raman Nagar",
    coord: [77.6680, 12.9831],
    verdict: "amber",
    reasons: [
      "Inner-east — DRDO labs, ITPL feeder routes",
      "Apartment supply across mid-range; quieter than Mahadevapura",
      "Through-traffic to ORR can affect inner-lane noise",
    ],
    priceRange: "₹8k–13k / sqft",
    confidenceScore: 50,
    tags: ["family-friendly", "east-blr", "mid-market"],
    sources: [],
  },
  {
    id: "blr-kammanahalli",
    name: "Kammanahalli",
    coord: [77.6402, 13.0156],
    verdict: "amber",
    reasons: [
      "North-east HBR layout extension — F&B / café strip on main road",
      "Walkable amenity density; mid-rise apartment stock",
      "Limited public-transit; commute by 2-wheeler / car",
    ],
    priceRange: "₹7k–12k / sqft",
    confidenceScore: 50,
    tags: ["walkable", "north-east", "mid-market"],
    sources: [],
  },
  {
    id: "blr-cooke-town",
    name: "Cooke Town",
    coord: [77.6189, 13.0040],
    verdict: "green",
    reasons: [
      "Cantonment heritage — colonial-era bungalows, walkable layout",
      "Adjacent to Frazer Town F&B + Pottery Town markets",
      "Heritage stock — apartment new-build inventory minimal",
    ],
    priceRange: "₹13k–20k / sqft",
    confidenceScore: 55,
    tags: ["heritage", "premium", "central", "walkable"],
    sources: [],
  },
  {
    id: "blr-kanakapura-road",
    name: "Kanakapura Road",
    coord: [77.5410, 12.8740],
    verdict: "amber",
    reasons: [
      "South corridor — apartment new-build + plot project cluster",
      "Yellow Line metro (Konanakunte → RV Road) opened in stretches",
      "Long corridor — verdict varies sharply by km marker",
    ],
    priceRange: "₹4k–8k / sqft",
    confidenceScore: 45,
    tags: ["south-corridor", "new-build", "outskirts"],
    sources: [],
  },
  {
    id: "blr-mysore-road",
    name: "Mysore Road",
    coord: [77.5040, 12.9510],
    verdict: "amber",
    reasons: [
      "South-west corridor — apartment + commercial mix on NH-275",
      "Mysore Road metro (Purple Line) terminus + Nayandahalli",
      "Highway-side residential noise + dust during construction",
    ],
    priceRange: "₹5k–9k / sqft",
    confidenceScore: 45,
    tags: ["south-west", "transit-hub", "affordable"],
    sources: [],
  },
];

/**
 * Public BLR verdicts — same records, priceData + investorEvidence
 * back-filled from the BLR pricing bank. Suburbs absent from the bank
 * fall through to band-only pricing (still works, just less precise).
 */
export const BLR_VERDICTS: Verdict[] = _BLR_RAW.map((v) => {
  const bank = getPricingFor(v.id);
  if (!bank) return v;
  return {
    ...v,
    priceData: v.priceData ?? bank.priceData,
    investorEvidence: v.investorEvidence ?? bank.investorEvidence,
  };
});
// Suppress "unused import" warning — BLR_SUBURB_PRICING is re-exported for
// any tooling that wants the raw bank without the override-applied helper.
void BLR_SUBURB_PRICING;

/**
 * Bengaluru-specific nearest-verdict lookup. Same shape as
 * `nearestVerdict`/`nearestVerdictMel` — no shared implementation per
 * the per-city duplication convention.
 */
export function nearestVerdictBlr(
  lng: number,
  lat: number,
  maxKm = 3,
): Verdict | null {
  let best: Verdict | null = null;
  let bestKm = Infinity;
  for (const v of BLR_VERDICTS) {
    const km = haversineKm(lat, lng, v.coord[1], v.coord[0]);
    if (km < bestKm) {
      bestKm = km;
      best = v;
    }
  }
  return bestKm <= maxKm ? best : null;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
